import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { ConfigurationService } from './configuration/configuration.service';
import { LocaleService } from './locale/locale.service';
import { SessionService } from './session.service';
import { ThemeService } from './theme.service';

import type { Subject } from 'rxjs';
import type { ConfigurationEndpoint } from './configuration/configuration.domain';

export const WINDOW_TOKEN = new InjectionToken<Window>('window');

export const BOOTSTRAP_CONFIGURATION = new InjectionToken<BootstrapConfiguration>('bootstrapConfiguration');

export interface BootstrapConfiguration {
  readonly configEndpoint?: ConfigurationEndpoint;
  readonly defaultConfig?: Record<string, unknown>;
  readonly minIeVersion?: number;
  readonly unsupportedBrowserUrl?: string;
  readonly locale: string; // used pre-bootstrap but could be changed based on configuration
  readonly theme: string;
  errorURL?: string;
}

export type PromiseFunc = () => Promise<unknown>;

export const BootstrapInitFactory = (bootstrapService: BootstrapService): PromiseFunc => (): Promise<unknown> => bootstrapService.bootstrap();

@Injectable({
  providedIn: 'root'
})
export class BootstrapService {

  private static readonly DEFAULT_ERROR_URL: string = '#page/errors/500';

  public readonly onBootstrapComplete: Subject<boolean>;
  private readonly document: Document;

  constructor(@Inject(WINDOW_TOKEN) private browserWindow: Window,
              @Inject(BOOTSTRAP_CONFIGURATION) @Optional() private bootstrapConfig: BootstrapConfiguration,
              @Inject(LocaleService) private localeService: LocaleService,
              @Inject(ThemeService) private themeService: ThemeService,
              @Inject(ConfigurationService) private configurationService: ConfigurationService,
              @Inject(SessionService) private preBootSessionService: SessionService) {
    this.document = this.browserWindow.document;
    this.onBootstrapComplete = new BehaviorSubject<boolean>(false);
  }

  bootstrap(): Promise<unknown> {
    this.verifyBrowserVersion();
    let { locale, theme }: BootstrapConfiguration = this.bootstrapConfig !== null ? this.bootstrapConfig : { locale: 'en_CA', theme: 'green' };
    this.setDefaultErrorURL();
    if (this.bootstrapConfig !== null && this.bootstrapConfig.configEndpoint !== undefined) {
      return this.configurationService
        .load(this.bootstrapConfig.configEndpoint, this.bootstrapConfig.defaultConfig)
        .then((): Promise<void | BootstrapConfiguration> => {
          locale = this.configurationService.getProperty('locale', locale) ?? locale;
          theme = this.configurationService.getProperty('theme', theme) ?? theme;
          return this.combineResults(locale, theme);
        }, (error: unknown): void => { // config call failed or session does not exist
          if (error instanceof HttpErrorResponse &&
            (this.preBootSessionService.hasSessionTimedOut(error.headers) || error.status === 401)) {
            // no session - send to login page
            this.preBootSessionService.redirectToLoginPage(error.headers);
          } else {
            this.localeService.setLocale(locale);
            this.themeService.setTheme(theme);
            return this.onBootstrapComplete.error(false);
          }
        });
    }
    return this.combineResults(locale, theme);
  }

  setDefaultErrorURL(): void {
    if (this.bootstrapConfig.errorURL === undefined) {
      this.bootstrapConfig.errorURL = BootstrapService.DEFAULT_ERROR_URL;
    }
  }

  private async combineResults(locale: string, theme: string): Promise<void | BootstrapConfiguration> {
    await Promise.all([this.localeService.setLocale(locale), this.themeService.setTheme(theme)]);
    this.onBootstrapComplete.next(true);
    this.onBootstrapComplete.complete();
  }

  private verifyBrowserVersion(): void {
    if (this.isUnsupportedBrowser(this.bootstrapConfig) && this.bootstrapConfig.unsupportedBrowserUrl !== undefined) {
      this.browserWindow.location.replace(this.bootstrapConfig.unsupportedBrowserUrl);
    }
  }

  private isUnsupportedBrowser(bootstrapConfig: BootstrapConfiguration): boolean {
    if (bootstrapConfig === null || typeof bootstrapConfig.minIeVersion !== 'number') {
      return false;
    }
    const minIeVersion: number = bootstrapConfig.minIeVersion;
    let ieVersion = 0;

    if (this.document.all !== undefined && window.atob !== undefined) {
      ieVersion = 10;
    }
    if (this.document.all !== undefined && window.atob === undefined) {
      ieVersion = 9;
    }
    if (this.document.all !== undefined && this.document.addEventListener === undefined) {
      ieVersion = 8;
    }
    if (this.document.all !== undefined && this.document.querySelector === undefined) {
      ieVersion = 7;
    }
    if (this.document.all !== undefined && window['XMLHttpRequest'] === undefined) {
      ieVersion = 6;
    }
    if (ieVersion === 0) {
      ieVersion = minIeVersion + 1;
    }
    return minIeVersion > ieVersion;
  }
}
